import React, { useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import FolderIcon from "@mui/icons-material/Folder";
import LabelIcon from "@mui/icons-material/Label";
import { useNavigate } from "react-router-dom";
import Title from "../../../../../components/Title";
import {
  useTagValues,
  useDeleteTagValue,
  useTagTypes,
} from "../../../../../api/tags/tags";
import { useSnackbar } from "../../../../../context/SnackbarContext";
import { useLoader } from "../../../../../context/LoaderContext";
import Back from "../../../../../components/Back";

function buildTagTypeTree(types, values) {
  const valueMap = {};
  values.forEach((v) => {
    valueMap[v.id] = { ...v, subRows: [] };
  });

  values.forEach((v) => {
    if (v.parent && valueMap[v.parent]) {
      valueMap[v.parent].subRows.push(valueMap[v.id]);
    }
  });

  return types.map((type) => ({
    ...type,
    type: "TagType",
    subRows: values
      .filter(
        (v) =>
          String(v.type) === String(type.id) &&
          (!v.parent || !valueMap[v.parent])
      )
      .map((v) => valueMap[v.id]),
  }));
}

export default function TagValueCatalog() {
  const navigate = useNavigate();
  const {
    data: values = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useTagValues();
  const { data: types = [] } = useTagTypes();
  const deleteTagValue = useDeleteTagValue();
  const { showSnackbar } = useSnackbar();
  const { showLoader, hideLoader } = useLoader();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const treeData = useMemo(
    () => buildTagTypeTree(types, values),
    [types, values]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 300,
        Cell: ({ row }) =>
          row.original.type === "TagType" ? (
            <Box display="flex" alignItems="center" sx={{ pl: row.depth * 2 }}>
              <FolderIcon sx={{ mr: 1 }} color="primary" />
              <Typography fontWeight={700}>{row.original.name}</Typography>
            </Box>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              sx={{ pl: row.depth * 2 + 3 }}
            >
              <LabelIcon sx={{ mr: 1 }} color="action" />
              <Typography>{row.original.name}</Typography>
            </Box>
          ),
      },
      {
        accessorKey: "id",
        header: "ID",
        size: 80,
      },
      {
        accessorKey: "type",
        header: "Tag Type",
        Cell: ({ row }) =>
          row.original.type === "TagType"
            ? "Tag Type"
            : types.find((t) => String(t.id) === String(row.original.type))
                ?.name || row.original.type,
        size: 120,
      },
    ],
    [types]
  );

  const handleCreate = () => navigate(`/management/products-tags/create`);
  const handleEdit = (row) => {
    const typeId =
      row.original.type === "TagType" ? row.original.id : row.original.type;
    navigate(`/management/products-tags/edit?id=${typeId}`);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setToDelete(null);
  };
  const handleDeleteClick = (id) => {
    setToDelete(id);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (toDelete !== null) {
      showLoader();
      try {
        await deleteTagValue.mutateAsync(toDelete);
        showSnackbar("Tag deleted!", "success");
        await refetch();
      } catch (e) {
        showSnackbar(e?.message || "Failed to delete", "error");
      }
      hideLoader();
    }
    setConfirmOpen(false);
    setToDelete(null);
  };

  const table = useMaterialReactTable({
    columns,
    data: treeData,
    getSubRows: (row) => row.subRows,
    enableExpanding: true,
    paginateExpandedRows: false,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
    }),
    muiTableContainerProps: { sx: { maxHeight: "calc(78vh - 188px)" } },
    enableRowActions: true,
    renderRowActionMenuItems: ({ row, closeMenu }) => [
      <MenuItem
        key="edit"
        onClick={() => {
          handleEdit(row);
          closeMenu();
        }}
      >
        Edit
      </MenuItem>,
      <MenuItem
        key="delete"
        onClick={() => {
          handleDeleteClick(row.original.id);
          closeMenu();
        }}
        sx={{ color: "error.main" }}
      >
        Delete
      </MenuItem>,
    ],
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <Button variant="contained" onClick={handleCreate}>
          Create Tag
        </Button>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={async () => {
                showLoader();
                try {
                  await refetch();
                } catch (e) {
                  showSnackbar(
                    "Failed to refresh: " + (e?.message || ""),
                    "error"
                  );
                }
                hideLoader();
              }}
              disabled={isFetching}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
    muiTableBodyRowProps: {
      children: isLoading ? (
        <tr>
          <td colSpan={columns.length} align="center">
            <CircularProgress size={28} sx={{ my: 2 }} />
          </td>
        </tr>
      ) : isError ? (
        <tr>
          <td colSpan={columns.length} align="center">
            <Typography color="error" p={2}>
              {error?.message || "Failed to load data"}
            </Typography>
          </td>
        </tr>
      ) : undefined,
    },
    enableColumnResizing: true,
    enableStickyHeader: true,
    manualPagination: false,
    layoutMode: "grid-no-grow",
    positionActionsColumn: "last",
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "compact",
      columnPinning: { right: ["mrt-row-actions"] },
      sorting: [
        {
          id: "id",
          desc: false,
        },
      ],
      columnVisibility: { sys_result: false },
    },
  });

  return (
    <>
      <Title>Products Tag Catalog</Title>
      <MaterialReactTable table={table} />
      <Back />

      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this tag? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteTagValue.isPending}
          >
            {deleteTagValue.isPending ? (
              <CircularProgress size={18} />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
